import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight, Check, Clapperboard, Download, Flag, Pause, Play, RotateCcw, ScanFace, ShoppingBag, X } from "lucide-react";
import { api, fmtXOF } from "../lib/api";
import { useTryOn } from "../context/TryOnContext";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";

const VIEWS = ["face", "profil", "dos"];

const View360 = ({ views }) => {
  const { t } = useTranslation();
  const ready = VIEWS.filter((v) => views[v]);
  const [angle, setAngle] = useState(0);
  const [auto, setAuto] = useState(true);
  const dragX = useRef(null);

  useEffect(() => {
    if (!auto || ready.length < 2) return undefined;
    let raf;
    const tick = () => {
      setAngle((a) => (a + 0.55) % 360);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [auto, ready.length]);

  const seg = 360 / ready.length;
  const idx = Math.floor(angle / seg) % ready.length;

  return (
    <div
      data-testid="tryon-viewer-360"
      onPointerDown={(e) => {
        dragX.current = e.clientX;
        setAuto(false);
      }}
      onPointerMove={(e) => {
        if (dragX.current == null) return;
        const d = e.clientX - dragX.current;
        dragX.current = e.clientX;
        setAngle((a) => (a + d * 1.5 + 360) % 360);
      }}
      onPointerUp={() => {
        dragX.current = null;
      }}
      onPointerLeave={() => {
        dragX.current = null;
      }}
      className="gold-frame frame-inner relative aspect-[3/4] w-full cursor-grab touch-pan-y select-none overflow-hidden border border-ligne bg-card2 active:cursor-grabbing"
    >
      {ready.map((v, i) => (
        <img
          key={v}
          src={views[v]}
          alt={`Essai 360° — vue ${v}`}
          draggable="false"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <button
        data-testid="tryon-autoplay-button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setAuto(!auto)}
        aria-label="360°"
        className="absolute end-4 top-4 flex h-10 w-10 items-center justify-center border border-ivoire/30 bg-noir/60 text-ivoire backdrop-blur-sm transition-colors hover:border-or hover:text-or"
      >
        {auto ? <Pause size={15} /> : <Play size={15} />}
      </button>
      <div className="pointer-events-none absolute inset-x-0 bottom-4 flex items-center justify-between px-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-ivoire/80">{t("tryon.viewHint")}</p>
        <div className="flex gap-1.5">
          {ready.map((v, i) => (
            <span key={v} className={`h-1.5 w-1.5 rotate-45 transition-colors duration-300 ${i === idx ? "bg-or" : "bg-ivoire/30"}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Essayage() {
  const { t } = useTranslation();
  const { items, toggle, clear } = useTryOn();
  const { addItem, openCart } = useCart();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("tous");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | busy | result
  const [progress, setProgress] = useState(0);
  const [views, setViews] = useState({});
  const [tryonId, setTryonId] = useState("");
  const [videoState, setVideoState] = useState("idle"); // idle | busy | ready
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const cats = [...new Set(products.map((p) => p.category))];
  const shown = cat === "tous" ? products : products.filter((p) => p.category === cat);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/tryon/upload", fd);
      setPhotoUrl(r.data.url);
      toast.success(t("tryon.photoOk"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tryon.errPhoto"));
    } finally {
      setUploading(false);
    }
  };

  const poll = (id) => {
    const timer = setInterval(async () => {
      try {
        const r = await api.get(`/tryon/status/${id}`);
        setProgress(Object.keys(r.data.views || {}).length);
        if (r.data.status === "termine") {
          clearInterval(timer);
          setViews(r.data.views);
          setPhase("result");
        } else if (r.data.status === "echec") {
          clearInterval(timer);
          toast.error(t("tryon.errGeneric"));
          setPhase("idle");
        }
      } catch {
        clearInterval(timer);
        toast.error(t("tryon.errGeneric"));
        setPhase("idle");
      }
    }, 4000);
  };

  const generate = async () => {
    if (phase !== "idle" || uploading) return;
    if (!items.length) return toast.error(t("tryon.emptyTenue"));
    if (!photoUrl) return toast.error(t("tryon.errPhoto"));
    if (!consent) return toast.error(t("tryon.needConsent"));
    setPhase("busy");
    setProgress(0);
    setVideoState("idle");
    setVideoUrl("");
    setShowVideo(false);
    try {
      const r = await api.post("/tryon/generate", { photo_url: photoUrl, product_ids: items.map((i) => i.id) });
      setTryonId(r.data.id);
      poll(r.data.id);
    } catch (err) {
      const d = String(err?.response?.data?.detail || "");
      toast.error(d.includes("gratuits") ? t("tryon.errDaily") : t("tryon.errGeneric"));
      setPhase("idle");
    }
  };

  const pollVideo = (id) => {
    const timer = setInterval(async () => {
      try {
        const r = await api.get(`/tryon/video/status/${id}`);
        if (r.data.status === "termine" && r.data.url) {
          clearInterval(timer);
          setVideoUrl(r.data.url);
          setVideoState("ready");
          setShowVideo(true);
        } else if (r.data.status === "echec") {
          clearInterval(timer);
          toast.error(t("tryon.videoError"));
          setVideoState("idle");
        }
      } catch {
        clearInterval(timer);
        toast.error(t("tryon.videoError"));
        setVideoState("idle");
      }
    }, 6000);
  };

  const requestVideo = async () => {
    if (videoState === "busy" || !tryonId) return;
    setVideoState("busy");
    try {
      const r = await api.post(`/tryon/video/${tryonId}`);
      if (r.data.status === "termine" && r.data.url) {
        setVideoUrl(r.data.url);
        setVideoState("ready");
        setShowVideo(true);
      } else {
        pollVideo(tryonId);
      }
    } catch {
      toast.error(t("tryon.videoError"));
      setVideoState("idle");
    }
  };

  const addTenueToCart = () => {
    items.forEach((p) => addItem(p, p.sizes?.includes("Unique") ? "Unique" : p.sizes?.[0] || "Unique"));
    toast.success(t("tryon.addedCart"));
    openCart();
  };

  const download = async () => {
    try {
      const blob = await (await fetch(views.face)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bkoss-essayage.png";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("tryon.errGeneric"));
    }
  };

  const shareUrl = views.face ? `${window.location.origin}${views.face}` : "";
  const shares = [
    { id: "whatsapp", href: `https://wa.me/?text=${encodeURIComponent(`${t("tryon.shareText")} ${shareUrl}`)}`, label: "WhatsApp" },
    { id: "facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, label: "Facebook" },
    { id: "x", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(t("tryon.shareText"))}&url=${encodeURIComponent(shareUrl)}`, label: "X" },
  ];

  return (
    <main data-testid="essayage-page" className="pt-32 lg:pt-44">
      <section className="mx-auto max-w-[1500px] px-6 pb-24 lg:px-12 lg:pb-32">
        <Reveal>
          <p className="eyebrow">{t("tryon.eyebrow")}</p>
          <h1 className="mt-4 font-serif text-4xl uppercase tracking-tight text-ivoire sm:text-5xl lg:text-6xl">
            {t("tryon.titlePre")} <span className="italic normal-case text-or">{t("tryon.titleIt")}</span>
          </h1>
          <p className="mt-6 max-w-2xl leading-relaxed text-argile">{t("tryon.intro")}</p>
        </Reveal>

        {phase === "result" && views.face ? (
          <div className="mt-14 grid gap-14 lg:grid-cols-2" data-testid="tryon-result">
            <Reveal>
              {showVideo && videoUrl ? (
                <video
                  data-testid="tryon-video-player"
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="gold-frame frame-inner aspect-[3/4] w-full border border-ligne bg-card2 object-cover"
                />
              ) : (
                <View360 views={views} />
              )}
              <button
                data-testid="tryon-video-button"
                onClick={() => (showVideo ? setShowVideo(false) : requestVideo())}
                className="mt-4 flex w-full items-center justify-center gap-3 border border-ligne px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ivoire transition-colors hover:border-or hover:text-or"
              >
                {videoState === "busy" ? <span className="h-3 w-3 animate-spin rounded-full border border-ligne border-t-or" /> : <Clapperboard size={13} />}
                {videoState === "busy" ? t("tryon.videoBusy") : showVideo ? t("tryon.backToViews") : t("tryon.videoButton")}
              </button>
              {videoState === "busy" && (
                <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-argile">{t("tryon.videoHint")}</p>
              )}
            </Reveal>
            <Reveal delay={0.15}>
              <p className="eyebrow">{t("tryon.resultEyebrow")}</p>
              <h2 className="mt-4 font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl">
                {t("tryon.resultTitle")}
              </h2>
              <div className="mt-8 flex flex-wrap gap-3">
                {items.map((p) => (
                  <span key={p.id} className="border border-ligne bg-card2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-argile">
                    {p.name}
                  </span>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <button data-testid="tryon-add-cart-button" onClick={addTenueToCart} className="group flex items-center gap-3 bg-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair">
                  {t("tryon.addToCart")} <ShoppingBag size={14} />
                </button>
                <button data-testid="tryon-download-button" onClick={download} className="flex items-center gap-3 border border-ligne px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ivoire transition-colors hover:border-or hover:text-or">
                  <Download size={14} /> {t("tryon.download")}
                </button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-ligne pt-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile">{t("tryon.share")}</span>
                {shares.map((s) => (
                  <a key={s.id} data-testid={`tryon-share-${s.id}`} href={s.href} target="_blank" rel="noreferrer" className="font-mono text-[11px] uppercase tracking-[0.2em] text-or transition-opacity hover:opacity-70">
                    {s.label}
                  </a>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6 text-argile">
                <button data-testid="tryon-modify-button" onClick={() => setPhase("idle")} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors hover:text-or">
                  <RotateCcw size={12} /> {t("tryon.modify")}
                </button>
                <a href="mailto:direction@bkoss.net?subject=B.KOSS%20—%20Essayage%20virtuel" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors hover:text-or">
                  <Flag size={12} /> {t("tryon.report")}
                </a>
              </div>
            </Reveal>
          </div>
        ) : phase === "busy" ? (
          <div className="mt-20 flex flex-col items-center py-16 text-center" data-testid="tryon-loading">
            <div className="relative h-20 w-20">
              <span className="absolute inset-0 animate-spin rounded-full border border-ligne border-t-or" />
              <span className="absolute inset-3 rotate-45 bg-or/10" />
            </div>
            <p className="mt-10 font-serif text-2xl italic text-ivoire">{t("tryon.generating")}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-argile">
              {t("tryon.progress", { n: progress })}
            </p>
          </div>
        ) : (
          <div className="mt-14 grid gap-14 lg:grid-cols-2">
            <Reveal>
              <p className="eyebrow">{t("tryon.step1")}</p>
              <h2 className="mt-3 font-serif text-2xl uppercase tracking-tight text-ivoire">{t("tryon.tenueTitle")}</h2>
              {items.length ? (
                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="tryon-tenue">
                  {items.map((p) => (
                    <div key={p.id} className="group relative border border-ligne bg-card2">
                      <img src={p.image} alt={p.name} className="aspect-[3/4] w-full object-cover" />
                      <button data-testid={`tryon-remove-${p.slug}`} onClick={() => toggle(p)} aria-label={t("common.remove")} className="absolute end-2 top-2 flex h-7 w-7 items-center justify-center bg-noir/80 text-ivoire opacity-0 transition-opacity hover:text-or group-hover:opacity-100">
                        <X size={13} />
                      </button>
                      <p className="truncate px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-argile">{p.name}</p>
                    </div>
                  ))}
                  <button data-testid="tryon-clear-tenue" onClick={clear} className="border border-dashed border-ligne font-mono text-[9px] uppercase tracking-[0.2em] text-argile transition-colors hover:border-or hover:text-or">
                    {t("tryon.clear")}
                  </button>
                </div>
              ) : (
                <p className="mt-6 text-sm italic text-argile">{t("tryon.emptyTenue")}</p>
              )}
              <div className="mt-8">
                <div className="flex flex-wrap gap-2">
                  {["tous", ...cats].map((c) => (
                    <button key={c} data-testid={`tryon-cat-${c}`} onClick={() => setCat(c)} className={`border px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] transition-colors ${cat === c ? "border-or bg-or/10 text-or" : "border-ligne text-argile hover:text-or"}`}>
                      {c === "tous" ? t("tryon.all") : c}
                    </button>
                  ))}
                </div>
                <div className="mt-5 grid max-h-[420px] grid-cols-3 gap-3 overflow-y-auto pe-2 sm:grid-cols-4 lg:grid-cols-3">
                  {shown.map((p) => {
                    const on = items.some((i) => i.id === p.id);
                    return (
                      <button key={p.id} data-testid={`tryon-product-${p.slug}`} onClick={() => toggle(p)} className={`relative border text-start transition-colors ${on ? "border-or" : "border-ligne hover:border-or/60"}`}>
                        <img src={p.image} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
                        {on && <span className="absolute end-2 top-2 flex h-6 w-6 items-center justify-center bg-or text-noir"><Check size={13} /></span>}
                        <p className="truncate px-2 py-1.5 font-mono text-[8px] uppercase tracking-[0.1em] text-argile">{p.name}</p>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-argile">{t("tryon.maxPieces")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="eyebrow">{t("tryon.step2")}</p>
              <h2 className="mt-3 font-serif text-2xl uppercase tracking-tight text-ivoire">{t("tryon.photoTitle")}</h2>
              <div className="mt-6 grid grid-cols-1 gap-3 text-argile sm:grid-cols-3">
                {["tip1", "tip2", "tip3"].map((k) => (
                  <p key={k} className="border border-ligne bg-card2 px-4 py-3 text-xs leading-relaxed">{t(`tryon.${k}`)}</p>
                ))}
              </div>
              <label data-testid="tryon-photo-zone" className="mt-6 flex cursor-pointer flex-col items-center justify-center gap-4 border border-dashed border-ligne px-6 py-14 text-center transition-colors hover:border-or">
                <input data-testid="tryon-photo-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="hidden" />
                {photoUrl ? (
                  <img src={photoUrl} alt="Votre photo" className="h-40 w-32 border border-ligne object-cover" />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center border border-ligne text-or"><ScanFace size={20} /></span>
                )}
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-argile">
                  {uploading ? t("common.loading") : photoUrl ? t("tryon.retake") : t("tryon.upload")}
                </span>
              </label>
              <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-argile">
                <input data-testid="tryon-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#C9A227]" />
                <span>{t("tryon.consent")}</span>
              </label>
              <motion.button
                data-testid="tryon-generate-button"
                onClick={generate}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-or px-8 py-5 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-colors hover:bg-orclair"
              >
                {t("tryon.generate")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </motion.button>
            </Reveal>
          </div>
        )}
      </section>
      <section className="border-t border-ligne bg-card2">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-6 px-6 py-16 lg:flex-row lg:items-center lg:px-12">
          <p className="max-w-xl font-serif text-xl italic text-argile">{t("tryon.footerNote")}</p>
          <Link data-testid="tryon-shop-link" to="/boutique" className="group flex items-center gap-3 border border-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-or transition-all hover:bg-or hover:text-noir">
            {t("tryon.shopCta")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </main>
  );
}